import { useInfiniteQuery } from 'react-query';

import {
  baseApiUrl,
  fetcher,
  ServerErrorResponse,
  createParamString,
  infiniteEmptyResults,
} from 'data_sources/constants';

import type { AccreditationVersion } from '../../_types/awb';
import type { PaginatedApiCollectionResponse } from 'types/api_response';

export async function getAccreditationVersions<T extends AccreditationVersion>(
  accreditationBodyId: string,
  params = {}
): Promise<PaginatedApiCollectionResponse<T>> {
  const url = `${baseApiUrl()}/awb/accreditation-body/v1/accreditation-bodies/${accreditationBodyId}/versions`;
  const paramString = new URLSearchParams(
    Object.entries({ ...params })
      .filter(([_k, v]) => v != null)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
  ).toString();

  const fullUrl = `${url}?${paramString}`;

  const data: PaginatedApiCollectionResponse<T> = await fetcher({
    url: fullUrl,
    method: 'get',
  });

  return data as PaginatedApiCollectionResponse<T>;
}

export default function useAccreditationVersions<T extends AccreditationVersion>(
  accreditationBodyId: string,
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/awb/accreditation-body/v1/accreditation-bodies/${accreditationBodyId}/versions`;

  return useInfiniteQuery<PaginatedApiCollectionResponse<T>, Error>(
    ['accreditation_versions', accreditationBodyId, params],
    async ({ pageParam, signal }): Promise<PaginatedApiCollectionResponse<T>> => {
      const paramString = new URLSearchParams(
        Object.entries({ ...params })
          .filter(([_k, v]) => v != null)
          .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
      ).toString();

      const fullUrl = pageParam || `${url}?${paramString}`;

      const data: PaginatedApiCollectionResponse<T> = await fetcher({
        url: fullUrl,
        method: 'get',
        options: { signal },
      });

      return data as PaginatedApiCollectionResponse<T>;
    },
    {
      ...options,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.nextToken && lastPage.content.length > 0) {
          const paramString = new URLSearchParams(
            Object.entries({
              ...params,
              nextToken: lastPage.nextToken,
            })
              .filter(([_k, v]) => v != null)
              .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
          ).toString();

          return `${url}?${paramString}`;
        }
      },
    }
  );
}
